/**
 * ForgotPassword — "Forgot password?" link + Firebase reset email (14 Oct 2026).
 * Uses the email already typed in the sign-in form (or asks for one). The message never says whether an
 * account exists for that email (so the form cannot be used to find accounts).
 */
import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase-config';
import { getRecaptchaToken } from '../../utils/recaptcha';

const box = { marginTop: 14, fontSize: 14, lineHeight: 1.45 };
const link = { background: 'none', border: 'none', padding: 0, color: '#4fc3f7', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #3a4a5e', background: 'rgba(255,255,255,0.06)', color: 'inherit', fontSize: 14, marginTop: 8 };
const btn = { marginTop: 8, padding: '9px 14px', borderRadius: 8, border: 'none', background: '#1e88e5', color: '#fff', cursor: 'pointer', fontSize: 14 };

export default function ForgotPassword({ email: typedEmail = '' }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const start = () => { setEmail(typedEmail || ''); setMsg(''); setErr(''); setOpen(true); };

  const send = async (e) => {
    e && e.preventDefault();
    const addr = (email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setErr('Please enter the email address you sign in with.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      try { await getRecaptchaToken('password_reset'); } catch (_) { /* the reset still works without it */ }
      await sendPasswordResetEmail(auth, addr);
      setMsg(`If an account exists for ${addr}, we have sent a link to reset the password. Please check your inbox and spam folder.`);
    } catch (e2) {
      if (e2 && e2.code === 'auth/user-not-found') {
        setMsg(`If an account exists for ${addr}, we have sent a link to reset the password. Please check your inbox and spam folder.`);
      } else if (e2 && e2.code === 'auth/too-many-requests') {
        setErr('Too many requests. Please wait a few minutes and try again.');
      } else if (e2 && e2.code === 'auth/invalid-email') {
        setErr('Please enter a valid email address.');
      } else {
        setErr('Could not send the reset email right now. Please try again, or contact research@cetalingua.com.');
      }
    } finally { setBusy(false); }
  };

  if (!open) {
    return <div style={box}><button type="button" style={link} onClick={start}>Forgot password?</button></div>;
  }
  return (
    <div style={box}>
      {msg ? <p style={{ margin: 0 }}>{msg}</p> : (
        <form onSubmit={send}>
          <div>Enter your sign-in email and we’ll send you a link to set a new password.</div>
          <input type="email" style={input} value={email} placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          {err && <div style={{ color: '#ef5350', marginTop: 6 }}>{err}</div>}
          <button type="submit" style={btn} disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>{' '}
          <button type="button" style={{ ...link, marginLeft: 10 }} onClick={() => setOpen(false)}>Cancel</button>
        </form>
      )}
    </div>
  );
}
